import { breakGameWithScoreByQtrID, breakHeadToHeadID, toHeadToHeadID } from '@shared/helpers';
import { HeadToHeadFirebaseCollection, HeadToHeadGameT } from '../types/game.types';
import { GameWithScoreByQtrID, HeadToHeadID, TeamID } from '../types/id.types';
import { ITeamPrediction } from '../types/prediction.types';

/**
 * Creates a line score object for prediction display purposes.
 *
 * @description
 * Generates an ITeamLineScore structure showing win probability in Q4 and predicted
 * score in totalPts. Q1-Q3 and OT are left as empty strings since predictions don't
 * include quarter-by-quarter breakdowns. Scores and probabilities are rounded to whole
 * numbers for display only; the source prediction retains its full precision.
 *
 * @param {TeamID} teamID - The team identifier
 * @param {string} teamAbv - The team abbreviation
 * @param {ITeamPrediction} teamPred - Team prediction data with score and win probability
 *
 * @return {ITeamLineScore} - Line score object formatted for display
 *
 * @example
 * const lineScore = getLineScore('11', 'DET', prediction);
 * // { teamID: '11', teamAbv: 'DET', Q1: ' ', Q2: ' ', Q3: ' ', Q4: '65%', totalPts: '28' }
 */
export const getLineScore = (teamID: TeamID, teamAbv: string, teamPred: ITeamPrediction) => {
  return {
    teamID,
    teamAbv,
    Q1: ' ',
    Q2: ' ',
    Q3: ' ',
    Q4: `${Math.round(teamPred.winProbability)}%`,
    OT: ' ',
    totalPts: Math.round(teamPred.predictedScore).toString(),
  };
};

export const convertGameWithScoreInHeadToHeadGameType = (gameWithScoreID: GameWithScoreByQtrID): HeadToHeadGameT => {
  const { gameID, awayTeamAbrvID, homeTeamAbrvID, awayTeamScore, homeTeamScore, seasonWeekID } =
    breakGameWithScoreByQtrID(gameWithScoreID);
  const headToheadID = toHeadToHeadID(awayTeamAbrvID, homeTeamAbrvID);
  const { team1AbrvID, team2AbrvID } = breakHeadToHeadID(headToheadID);
  const team1Score = team1AbrvID === awayTeamAbrvID ? awayTeamScore : homeTeamScore;
  const team2Score = team2AbrvID === homeTeamAbrvID ? homeTeamScore : awayTeamScore;
  const isTie = awayTeamScore === homeTeamScore;
  const winnerTeamAbrvID = isTie ? 'TIE' : homeTeamScore > awayTeamScore ? homeTeamAbrvID : awayTeamAbrvID;
  return {
    gameID,
    team1AbrvID,
    team1Score,
    team2AbrvID,
    team2Score,
    winnerTeamAbrvID,
    seasonWeekID,
    marginOfVictory: Math.abs(team1Score - team2Score),
  };
};

export const generateH2HCollectionsByGamesWithScoreIDs = (
  gameWithScoreIDs: GameWithScoreByQtrID[],
): Record<HeadToHeadID, HeadToHeadFirebaseCollection> => {
  const h2hCollections: Record<HeadToHeadID, HeadToHeadFirebaseCollection> = {};

  gameWithScoreIDs.forEach((gameWithScoreID) => {
    const h2hGame = convertGameWithScoreInHeadToHeadGameType(gameWithScoreID);
    const { team1AbrvID, team2AbrvID } = h2hGame;
    const headToHeadID: HeadToHeadID = toHeadToHeadID(team1AbrvID, team2AbrvID);

    if (!h2hCollections[headToHeadID]) {
      h2hCollections[headToHeadID] = {
        headToHeadID,
        team1AbrvID,
        team2AbrvID,
        allTimeRecord: {
          team1Wins: 0,
          team2Wins: 0,
          ties: 0,
        },
        games: [],
      };
    }

    const collection = h2hCollections[headToHeadID];

    // Update win/loss/tie records
    if (h2hGame.winnerTeamAbrvID === 'TIE') {
      collection.allTimeRecord.ties += 1;
    } else if (h2hGame.winnerTeamAbrvID === team1AbrvID) {
      collection.allTimeRecord.team1Wins += 1;
    } else if (h2hGame.winnerTeamAbrvID === team2AbrvID) {
      collection.allTimeRecord.team2Wins += 1;
    }

    // Add game to the list
    collection.games.push(h2hGame);
  });

  return h2hCollections;
};
